import { useEffect, useState } from "react"
import { Field, Form, Formik } from "formik"
import api from "../services/api"
import { phoneErrorMessage } from "../services/auth"
import FormikField from "../Components/Forms/FormikField";
import * as Yup from "yup"
import Button from "../Components/Button";
import { userAtom } from "../state";
import { useAtom } from "jotai";
import { Paragraph } from "../Components/Paragraph";
import { useOrganizations } from "../hooks/useOrganizations";
import OtpInput from "../Components/Forms/OtpInput";


const schema = Yup.object().shape({
    first_name: Yup.string().required('Required'),
    last_name: Yup.string().required('Required'),
    email: Yup.string().email('Invalid email').required('Required')
});

function EditAccount({onAccountEdited}) {
    const [user, setUser] = useAtom(userAtom)
    const { organizations } = useOrganizations()

    const [phoneEnabled, setPhoneEnabled] = useState(false)
    const [phone, setPhone] = useState(user.phone_e164 || "")
    const [phoneStage, setPhoneStage] = useState(user.phone_verified_at ? "verified" : "idle")
    const [phoneCode, setPhoneCode] = useState("")
    const [phoneError, setPhoneError] = useState(null)
    const [phoneBusy, setPhoneBusy] = useState(false)

    useEffect(() => {
        api.getConfig()
            .then(response => setPhoneEnabled(response.data.phone_login_enabled))
            .catch(() => setPhoneEnabled(false))
    }, [])

    const requestPhoneCode = () => {
        setPhoneBusy(true)
        setPhoneError(null)
        api.requestPhoneLink(phone)
            .then(() => {
                setUser({ ...user, phone_verified_at: null })
                setPhoneStage("code")
            })
            .catch(error =>
                setPhoneError(phoneErrorMessage(error, "phone", "We couldn't send a code to that number.")),
            )
            .finally(() => setPhoneBusy(false))
    }

    const verifyPhoneCode = () => {
        setPhoneBusy(true)
        setPhoneError(null)
        api.verifyPhoneLink(phoneCode)
            .then(response => {
                setUser(response.data)
                setPhoneStage("verified")
                setPhoneCode("")
            })
            .catch(error =>
                setPhoneError(phoneErrorMessage(error, "code", "That code is incorrect or has expired.")),
            )
            .finally(() => setPhoneBusy(false))
    }

    const onSubmit = (values, {setErrors}) => {
        const payload = {
            first_name: values.first_name,
            last_name: values.last_name,
            email: values.email,
            password: values.password,
        }
        if (!user.is_admin) {
            payload.organization_id = values.organization_id || null
        }
        api.updateUser(user.id, payload).then((response) => {
            setUser(response.data)
            onAccountEdited();
        }).catch((error) => {
            setErrors(error.response.data.errors);
        });
    }


    return (
        <div className="mx-auto flex max-w-xl flex-col items-center justify-center">
            <h1 className="mb-6 text-center text-2xl font-bold">Account Settings</h1>
            <Paragraph className="text-center">
                Update your account information below.
            </Paragraph>

            <Formik
                onSubmit={onSubmit}
                initialValues={{
                    first_name: user.first_name || '',
                    last_name: user.last_name || '',
                    email: user.email,
                    organization_id: user.organizations?.[0]?.id ?? '',
                }}
                validationSchema={schema}
                validateOnChange={false}
                validateOnBlur={false}
            >
                {({values}) => (
                    <Form className="w-full">
                        <FormikField name="first_name" title="First Name" instructions={[]}>
                            <Field
                                name="first_name"
                                className="w-full rounded-md border-0 bg-neutral-100 p-4 mb-4"
                            />
                        </FormikField>
                        <FormikField name="last_name" title="Last Name" instructions={[]}>
                            <Field
                                name="last_name"
                                className="w-full rounded-md border-0 bg-neutral-100 p-4 mb-4"
                            />
                        </FormikField>
                        <FormikField name="email" title="Email" instructions={[]}>
                            <Field
                                name="email"
                                className="w-full rounded-md border-0 bg-neutral-100 p-4 mb-4"
                            />
                        </FormikField>
                        {!user.is_admin && (
                            <FormikField name="organization_id" title="Organization" instructions={[]}>
                                {user.organizations?.length > 0 ? (
                                    <div className="w-full rounded-md border-0 bg-neutral-100 p-4 mb-4 text-neutral-500">
                                        {user.organizations.map(o => o.name).join(', ') || '—'}
                                    </div>
                                ) : (
                                    <Field
                                        as="select"
                                        name="organization_id"
                                        className="w-full rounded-md border-0 bg-neutral-100 p-4 mb-4"
                                    >
                                        <option value="">No organization</option>
                                        {organizations.map(org => (
                                            <option key={org.id} value={org.id}>{org.name}</option>
                                        ))}
                                    </Field>
                                )}
                            </FormikField>
                        )}
                        <div className="mt-6 flex justify-center">
                            <Button type="submit" width="w-64">
                                Update Account
                            </Button>
                        </div>
                    </Form>
                )}
            </Formik>

            {phoneEnabled && (
            <div className="mt-10 w-full border-t border-neutral-300 pt-8">
                <h2 className="mb-2 text-lg font-bold">Phone number</h2>
                <Paragraph className="text-sm">
                    Optional. Add a verified phone number to log in with a text message instead of email.
                </Paragraph>

                {phoneStage === "verified" ? (
                    <div className="mt-4">
                        <p className="mb-2 text-sm">
                            <span className="font-bold">{user.phone_e164}</span>{" "}
                            <span className="text-green-700">— verified</span>
                        </p>
                        <button
                            type="button"
                            onClick={() => setPhoneStage("idle")}
                            className="text-sm text-brand underline"
                        >
                            Change number
                        </button>
                    </div>
                ) : phoneStage === "code" ? (
                    <div className="mt-4">
                        <p className="mb-2 text-sm text-gray-700">
                            We sent a code to <strong>{phone}</strong>
                        </p>
                        <OtpInput value={phoneCode} onChange={setPhoneCode} disabled={phoneBusy} autoFocus />
                        {phoneError && <p className="mb-4 text-sm text-red-600">{phoneError}</p>}
                        <div className="flex items-center gap-4">
                            <Button onClick={verifyPhoneCode} disabled={phoneBusy || phoneCode.length < 6}>
                                Verify
                            </Button>
                            <button
                                type="button"
                                onClick={() => {
                                    setPhoneStage("idle")
                                    setPhoneCode("")
                                    setPhoneError(null)
                                }}
                                className="text-sm text-brand underline"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="mt-4">
                        {user.phone_verified_at && (
                            <p className="mb-2 text-sm text-gray-700">
                                Sending a code will remove verification from your current number until you confirm the new one.
                            </p>
                        )}
                        <input
                            type="tel"
                            autoComplete="tel"
                            value={phone}
                            onChange={event => setPhone(event.target.value)}
                            className="mb-4 w-full rounded-md border-0 bg-neutral-100 p-4"
                            placeholder="Enter your phone number"
                            aria-label="Phone number"
                        />
                        {phoneError && <p className="mb-4 text-sm text-red-600">{phoneError}</p>}
                        <div className="flex items-center gap-4">
                            <Button onClick={requestPhoneCode} disabled={phoneBusy || phone.length === 0}>
                                Send Code
                            </Button>
                            {user.phone_verified_at && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setPhoneStage("verified")
                                        setPhone(user.phone_e164 || "")
                                        setPhoneError(null)
                                    }}
                                    className="text-sm text-brand underline"
                                >
                                    Cancel
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
            )}
        </div>
    )
  }

  export default EditAccount
  